import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Facebook, Instagram, Youtube } from './SocialIcons';
import { useSettings } from '../context/SettingsContext';
import axios from 'axios';

const FALLBACK_DARK_LOGO = '/logo-dark.png';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const [quickLinks, setQuickLinks] = useState([]);
  const [serviceLinks, setServiceLinks] = useState([]);
  const lang = i18n.language?.substring(0, 2) || 'en';

  useEffect(() => {
    const loadPages = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/pages`);
        const pages = response.data;
        setQuickLinks(pages.filter(p => p.page_type === 'page'));
        setServiceLinks(pages.filter(p => p.page_type === 'service'));
      } catch (e) {
        console.error('Failed to load pages for footer');
      }
    };
    loadPages();
  }, []);

  return (
    <footer className="bg-gray-900 text-gray-300 dark:bg-[#171717]" data-testid="footer">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="mb-4">
              <img src={settings.logo_url || FALLBACK_DARK_LOGO} alt={lang === 'ar' && settings.site_name_ar ? settings.site_name_ar : settings.site_name || 'Itronix Electronics'} className="h-12 w-auto" />
            </div>
            <p className="text-sm font-semibold text-white mb-1" data-testid="footer-site-name">{lang === 'ar' && settings.site_name_ar ? settings.site_name_ar : settings.site_name || 'Itronix Electronics'}</p>
            <p className="text-sm mb-4" data-testid="footer-tagline">{lang === 'ar' && settings.tagline_ar ? settings.tagline_ar : settings.tagline || t('footer.description')}</p>
            <div className="flex space-x-3">
              {settings.facebook && (
                <a href={settings.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"><Facebook className="h-4 w-4" /></a>
              )}
              {settings.instagram && (
                <a href={settings.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"><Instagram className="h-4 w-4" /></a>
              )}
              {settings.youtube && (
                <a href={settings.youtube} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"><Youtube className="h-4 w-4" /></a>
              )}
              {settings.email && (
                <a href={`mailto:${settings.email}`} className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"><Mail className="h-4 w-4" /></a>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-white font-bold mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-red-500 transition-colors">{t('footer.links.home')}</Link></li>
              <li><Link to="/shop" className="hover:text-red-500 transition-colors">{t('footer.links.shop')}</Link></li>
              <li><Link to="/contact" className="hover:text-red-500 transition-colors">{t('footer.links.contact')}</Link></li>
              {quickLinks.map(page => (
                <li key={page.id}><Link to={`/page/${page.slug}`} className="hover:text-red-500 transition-colors">{page.title?.[lang] || page.title?.en || page.slug}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold mb-4">{t('footer.services')}</h3>
            <ul className="space-y-2 text-sm">
              {serviceLinks.map(page => (
                <li key={page.id}><Link to={`/page/${page.slug}`} className="hover:text-red-500 transition-colors">{page.title?.[lang] || page.title?.en || page.slug}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold mb-4">{t('footer.contactUs')}</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start space-x-3" data-testid="footer-address"><MapPin className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" /><span>{lang === 'ar' && settings.address_ar ? settings.address_ar : settings.address}</span></li>
              <li className="flex items-center space-x-3" data-testid="footer-phone"><Phone className="h-5 w-5 text-red-500 flex-shrink-0" /><span>{lang === 'ar' && settings.phone_ar ? settings.phone_ar : settings.phone}</span></li>
              <li className="flex items-center space-x-3" data-testid="footer-email"><Mail className="h-5 w-5 text-red-500 flex-shrink-0" /><span>{settings.email}</span></li>
              <li className="flex items-start space-x-3" data-testid="footer-hours"><Clock className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" /><span>{lang === 'ar' && settings.hours_ar ? settings.hours_ar : settings.hours}</span></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm"><p>&copy; {new Date().getFullYear()} {lang === 'ar' && settings.site_name_ar ? settings.site_name_ar : settings.site_name || 'Itronix Electronics'}. {t('footer.allRightsReserved')}</p></div>
      </div>
    </footer>
  );
};

export default Footer;
